import { FadeUp, AnimatedLine, StaggerContainer, StaggerItem } from "../components/Animations";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";

const PakistanPage = () => {
  const sectors = [
    {
      title: "Poultry Breeding & Production",
      image: "https://images.unsplash.com/photo-1548550023-2bdb3c5beed7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      description: "Operating in poultry breeding, farming, and distribution of poultry products and seeds. Developing and managing poultry farms, hatcheries, breeder facilities, and agricultural support infrastructure to ensure efficient production and supply operations.",
      capabilities: [
        { label: "Poultry Breeding", value: "Commercial Scale" },
        { label: "Hatcheries", value: "Modern Facilities" },
        { label: "Distribution", value: "Regional Network" },
        { label: "Infrastructure", value: "Integrated Operations" }
      ],
      services: [
        "Poultry breeding programs",
        "Commercial egg production",
        "Hatchery operations",
        "Agricultural seed distribution"
      ]
    },
    {
      title: "Agricultural Development",
      image: "https://images.unsplash.com/photo-1682368724572-61e83de6c91f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      description: "Focusing on breeding, raising, and distribution of poultry and related agricultural products. Supporting development of poultry farms, hatcheries, greenhouses, and distribution centres to strengthen sustainable agricultural production.",
      capabilities: [
        { label: "Breeding", value: "Quality Genetics" },
        { label: "Greenhouses", value: "Controlled Environment" },
        { label: "Distribution", value: "Supply Chain" },
        { label: "Sustainability", value: "Eco-Focused" }
      ],
      services: [
        "Poultry farm development",
        "Breeder management",
        "Greenhouse operations",
        "Distribution centre management"
      ]
    },
  ];

  return (
    <div data-testid="pakistan-page">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="container-wide">
          <FadeUp>
            <Link 
              to="/portfolio" 
              className="inline-flex items-center gap-2 text-stone hover:text-charcoal transition-colors duration-300 mb-8"
              data-testid="back-to-portfolio"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-sans uppercase tracking-wider">Global Portfolio</span>
            </Link>
          </FadeUp>

          <FadeUp delay={0.1}>
            <span className="text-caption text-bronze mb-6 block">Pakistan</span>
            <h1 className="text-display text-charcoal max-w-5xl">
              Agriculture & Poultry Operations
            </h1>
          </FadeUp>

          <FadeUp delay={0.2} className="mt-12 max-w-3xl">
            <p className="text-xl md:text-2xl font-sans font-light text-stone leading-relaxed">
              Strategic investments in poultry breeding, farming, and agricultural infrastructure — 
              contributing to food security and sustainable agricultural production.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Pakistan Market Context */}
      <section className="pb-24 md:pb-32">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeUp>
              <div className="aspect-[4/3] relative overflow-hidden border-glass">
                <img
                  src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Agricultural landscape"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-bronze/5" />
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <span className="text-caption text-bronze mb-6 block">Market Context</span>
              <h2 className="text-headline text-charcoal mb-8">
                Essential Infrastructure
              </h2>
              <p className="text-body text-lg mb-6">
                Pakistan's poultry sector is one of the fastest-growing agricultural 
                segments, serving as a critical source of affordable protein for a 
                population exceeding 230 million.
              </p>
              <p className="text-body mb-6">
                Our investments in poultry farming and agricultural infrastructure 
                address fundamental food security needs while building sustainable, 
                efficient operations for long-term value creation.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="p-4 bg-sand/50">
                  <span className="text-2xl font-serif text-bronze">230M+</span>
                  <p className="text-xs text-stone mt-1">Population Served</p>
                </div>
                <div className="p-4 bg-sand/50">
                  <span className="text-2xl font-serif text-bronze">Integrated</span>
                  <p className="text-xs text-stone mt-1">Operations</p>
                </div>
                <div className="p-4 bg-sand/50">
                  <span className="text-2xl font-serif text-bronze">Sustainable</span>
                  <p className="text-xs text-stone mt-1">Practices</p>
                </div>
                <div className="p-4 bg-sand/50">
                  <span className="text-2xl font-serif text-bronze">Regional</span>
                  <p className="text-xs text-stone mt-1">Distribution</p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Industry Sectors */}
      <section className="section-spacing bg-white grain">
        <div className="container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-6 block">Industry Sectors</span>
            <h2 className="text-headline text-charcoal mb-16">
              Agricultural Operations
            </h2>
          </FadeUp>

          <StaggerContainer className="space-y-24" staggerDelay={0.2}>
            {sectors.map((sector, index) => (
              <StaggerItem key={sector.title}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                  {/* Image */}
                  <div className={`lg:col-span-5 ${index % 2 === 1 ? 'lg:col-start-8' : ''}`}>
                    <div className="aspect-[4/3] relative overflow-hidden border-glass card-reflect sticky top-32">
                      <img 
                        src={sector.image} 
                        alt={sector.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-charcoal/10" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`lg:col-span-6 ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : 'lg:col-start-7'}`}>
                    <span className="text-mono text-stone/60 block mb-4">0{index + 1}</span>
                    <h3 className="text-2xl md:text-3xl font-serif text-charcoal mb-6">
                      {sector.title}
                    </h3>
                    <AnimatedLine className="mb-8" />
                    <p className="text-body text-lg mb-8">{sector.description}</p>
                    
                    {/* Capabilities Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-8">
                      {sector.capabilities.map((cap, i) => (
                        <div key={i} className="text-center p-4 bg-sand/30">
                          <span className="text-sm font-medium text-charcoal block">{cap.value}</span>
                          <span className="text-xs text-stone">{cap.label}</span>
                        </div>
                      ))}
                    </div>

                    <h4 className="text-sm font-sans uppercase tracking-wider text-bronze mb-6">Core Operations</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {sector.services.map((service, i) => (
                        <div key={i} className="flex items-center gap-3 p-4 bg-sand/20">
                          <span className="w-2 h-2 bg-bronze flex-shrink-0" />
                          <span className="text-sm text-charcoal">{service}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Sustainability Focus */}
      <section className="section-spacing bg-charcoal">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <FadeUp>
                <span className="text-caption text-bronze mb-6 block">Sustainability</span>
                <h2 className="text-headline text-champagne">
                  Building for the future.
                </h2>
              </FadeUp>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <FadeUp delay={0.1}>
                <p className="text-lg font-sans font-light text-champagne/80 mb-8">
                  Our agricultural operations are designed with sustainability at their core — 
                  efficient resource utilisation, responsible farming practices, and long-term 
                  environmental stewardship.
                </p>
              </FadeUp>

              <FadeUp delay={0.2}>
                <p className="text-body text-champagne/60 mb-8">
                  By investing in modern infrastructure, quality genetics, and professional 
                  management, we're building agricultural businesses that can deliver 
                  consistent results across market cycles while contributing to 
                  Pakistan's food security.
                </p>
              </FadeUp>

              <FadeUp delay={0.3}>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <span className="w-2 h-2 bg-bronze" />
                    <span className="text-sm text-champagne/80">Modern breeding and farming facilities</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="w-2 h-2 bg-bronze" />
                    <span className="text-sm text-champagne/80">Integrated supply chain operations</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="w-2 h-2 bg-bronze" />
                    <span className="text-sm text-champagne/80">Sustainable agricultural practices</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="w-2 h-2 bg-bronze" />
                    <span className="text-sm text-champagne/80">Regional distribution networks</span>
                  </div>
                </div>
              </FadeUp>

              <FadeUp delay={0.4} className="mt-8">
                <Link
                  to="/philosophy"
                  className="inline-flex items-center gap-3 text-champagne hover:text-bronze transition-colors duration-500"
                  data-testid="link-to-philosophy"
                >
                  <span className="text-sm font-sans font-medium uppercase tracking-wider">Read Our Investment Philosophy</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* Cross Links */}
      <section className="section-spacing bg-sand/30">
        <div className="container-wide">
          <FadeUp>
            <h3 className="text-headline text-charcoal mb-12 text-center">
              Explore Our Global Portfolio
            </h3>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FadeUp delay={0.1}>
              <Link 
                to="/portfolio/uae" 
                className="group block border-glass card-reflect shimmer p-8 transition-all duration-500"
                data-testid="link-to-uae"
              >
                <span className="text-mono text-stone/60 mb-2 block">United Arab Emirates</span>
                <h4 className="text-2xl font-serif text-charcoal group-hover:text-bronze transition-colors duration-500 mb-4">
                  Investment & Management
                </h4>
                <p className="text-body text-sm mb-4">Strategic advisory and commercial management services</p>
                <span className="inline-flex items-center gap-2 text-bronze text-sm">
                  Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </FadeUp>
            <FadeUp delay={0.2}>
              <Link 
                to="/portfolio/uk" 
                className="group block border-glass card-reflect shimmer p-8 transition-all duration-500"
                data-testid="link-to-uk"
              >
                <span className="text-mono text-stone/60 mb-2 block">United Kingdom</span>
                <h4 className="text-2xl font-serif text-charcoal group-hover:text-bronze transition-colors duration-500 mb-4">
                  Media, Automotive & Hospitality
                </h4>
                <p className="text-body text-sm mb-4">Film production, automotive services, and dining experiences</p>
                <span className="inline-flex items-center gap-2 text-bronze text-sm">
                  Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 border-t border-sand">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <Link
              to="/portfolio/uk"
              className="inline-flex items-center gap-3 text-charcoal hover:text-bronze transition-colors duration-500"
              data-testid="nav-prev-uk"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-sans font-medium uppercase tracking-wider">United Kingdom</span>
            </Link>

            <Link
              to="/philosophy"
              className="inline-flex items-center gap-3 text-charcoal hover:text-bronze transition-colors duration-500"
              data-testid="nav-next-philosophy"
            >
              <span className="text-sm font-sans font-medium uppercase tracking-wider">Investment Philosophy</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PakistanPage;
